import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const normalizeUser = (u) => {
    if (!u) return u;
    const roleValue = typeof u.role === 'object' && u.role !== null ? u.role.name : u.role;
    return { ...u, role: roleValue };
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token and get user info
      // Try to get profile from employee endpoint first, then manager if that fails
      authAPI.getProfile()
        .then(response => {
          console.log('Employee profile success:', response.data);
          setUser(normalizeUser(response.data));
          setLoading(false);
        })
        .catch(error => {
          console.log('Employee profile failed, trying manager profile:', error.response?.status);
          // If employee profile fails, try manager profile
          if (error.response?.status === 403 || error.response?.status === 404) {
            return authAPI.getManagerProfile();
          }
          throw error;
        })
        .then(response => {
          if (response) {
            console.log('Manager profile success:', response.data);
            setUser(normalizeUser(response.data));
          }
        })
        .catch(error => {
          console.error('Token verification failed:', error);
          // Only remove token if it's a 401 (unauthorized) error
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            setUser(null);
          } else {
            // For other errors (like 403), keep the token but set user to null
            // This allows the user to try logging in again
            setUser(null);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, ...userData } = response.data;
      
      localStorage.setItem('token', token);
      setUser(normalizeUser(userData));
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      // Check for specific error messages from backend
      const message = error.response?.data?.error || error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast.success('Logged out successfully');
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      toast.success('Registration successful! Please login.');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    login,
    logout,
    register,
    loading,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
    isAdmin: user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN',
    isEmployee: user?.role === 'EMPLOYEE',
    isManager: user?.role === 'MANAGER',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

